import {
  Box, Button, Form, SidebarModal,
} from '@domain-group/suit-theme'

import useGeoCountry, { useGeoCountryForm } from '@hooks/useGeoCountry'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { GeoCountryModel } from '@models/types'

import type { ActionResult } from '@redux/modules/types'

type ModalCallbacks = {
  closeModal: () => void,
  createGeoCountry: (payload: HandleCallbackActionParams) => Promise<ActionResult<GeoCountryModel>>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateGeoCountryModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'CreateGeoCountryModal', showModal } = props
  const { closeModal, createGeoCountry } = callbacks

  const {
    callbacks: { createGeoCountry: createFn },
    creating,
  } = useGeoCountry()

  const {
    entityState,
    entityState: {
      active,
      calling_code,
      code,
      currency_code,
      currency_symbol,
      tax_rate,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useGeoCountryForm({}, { validateOn: 'change' })

  const createGeoCountryPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Country Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Country" />

      <SidebarModal.Content>
        <Box flexDirection="column" flexShrink={0}>
          <Form.Field
            boxProps={{ marginBottom: 'large' }}
            direction="column"
            errorMessage={errors.title}
            label="* Title"
          >
            <input
              name="title"
              type="text"
              value={title}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            boxProps={{ marginBottom: 'large' }}
            direction="column"
            errorMessage={errors.code}
            label="* Country Code"
          >
            <input
              name="code"
              type="text"
              value={code}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            boxProps={{ marginBottom: 'large' }}
            direction="column"
            errorMessage={errors.currency_code}
            label="Currency"
          >
            <input
              name="currency_code"
              type="text"
              value={currency_code}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            boxProps={{ marginBottom: 'large' }}
            direction="column"
            errorMessage={errors.currency_symbol}
            label="Currency Symbol"
          >
            <input
              name="currency_symbol"
              type="text"
              value={currency_symbol}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            boxProps={{ marginBottom: 'large' }}
            direction="column"
            errorMessage={errors.calling_code}
            label="Calling Code"
          >
            <input
              name="calling_code"
              type="text"
              value={calling_code}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            boxProps={{ marginBottom: 'large' }}
            direction="column"
            errorMessage={errors.tax_rate}
            label="Tax Rate(%)"
          >
            <input
              data-value-type="number"
              min="0"
              name="tax_rate"
              type="number"
              value={tax_rate}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Active">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="active"
              value={active}
              {...handlers}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </Form.Field>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createGeoCountry(createGeoCountryPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Country' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateGeoCountryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
